import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  DialogActions,
  DialogContent,
  TextField
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../components/Dialog/Dialog";
import ActionButton from "../../components/Button/ActionButton";
import HelpButton from "../../components/payments/HelpContent/HelpButton";
import { Procedure } from "@kenettotech/scribo-shared-functions";
import useProcedureSearch from "../../hooks/useProcedureSearch";
import { PaymentLine } from "../../hooks/useRequestPayment";
import { parseDecimal } from "../../utils/money";
import { MAX_LINE_UNIT_COST } from "../../types/payments";
import {
  MAX_LINE_QUANTITY_DIGITS,
  MAX_LINE_UNIT_COST_DIGITS,
  PAYMENTS_HELP_CONTENT
} from "../../constants";
import { outlinedFieldSx } from "../../utils/fieldStyles";

/** Generates the client side id used to track a line before it is submitted. */
function generateLineId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

export default function AddPaymentLineDialog({
  open,
  occupation,
  onClose,
  onAdd
}: {
  open: boolean;
  occupation?: number;
  onClose: () => void;
  onAdd: (line: PaymentLine) => void;
}) {
  const { t } = useTranslation();
  const { search, loading } = useProcedureSearch(occupation);

  const [query, setQuery] = useState("");
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [unitCost, setUnitCost] = useState("");
  const [quantity, setQuantity] = useState("1");

  useEffect(() => {
    if (open) return;
    setQuery("");
    setProcedure(null);
    setUnitCost("");
    setQuantity("1");
  }, [open]);

  const results = useMemo(() => (loading ? [] : search(query)), [
    query,
    search,
    loading
  ]);

  const parsedUnitCost = parseDecimal(unitCost);
  const parsedQuantity = Number.parseInt(quantity, 10);
  const isValid =
    Boolean(procedure) &&
    !Number.isNaN(parsedUnitCost) &&
    parsedUnitCost <= MAX_LINE_UNIT_COST &&
    !Number.isNaN(parsedQuantity) &&
    parsedQuantity >= 1;

  const onConfirm = useCallback(() => {
    if (!procedure) return;
    onAdd({
      id: generateLineId(),
      code: procedure.id,
      description: procedure.name,
      unitCost: parsedUnitCost,
      quantity: parsedQuantity
    });
    onClose();
  }, [procedure, parsedUnitCost, parsedQuantity, onAdd, onClose]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={t("requestPayment.addLine")}
    >
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <HelpButton linkName={PAYMENTS_HELP_CONTENT.ADD_LINE} />
          </Box>

          <Autocomplete
            options={results}
            loading={loading}
            value={procedure}
            onChange={(_, value) => setProcedure(value)}
            inputValue={query}
            onInputChange={(_, value) => setQuery(value)}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            // Lunr already ranks and filters; keep MUI from filtering again.
            filterOptions={options => options}
            noOptionsText={t("requestPayment.noProcedures")}
            renderInput={params => (
              <TextField
                {...params}
                label={t("requestPayment.procedure")}
                placeholder={t("requestPayment.procedurePlaceholder")}
                sx={outlinedFieldSx}
              />
            )}
          />

          <TextField
            label={t("requestPayment.unitCost")}
            value={unitCost}
            onChange={event => setUnitCost(event.target.value)}
            inputProps={{
              maxLength: MAX_LINE_UNIT_COST_DIGITS,
              inputMode: "decimal"
            }}
            fullWidth
            sx={outlinedFieldSx}
          />

          <TextField
            label={t("requestPayment.quantity")}
            value={quantity}
            onChange={event => setQuantity(event.target.value)}
            inputProps={{
              maxLength: MAX_LINE_QUANTITY_DIGITS,
              inputMode: "numeric"
            }}
            fullWidth
            sx={outlinedFieldSx}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("requestPayment.add")}
            onClick={onConfirm}
            disabled={!isValid}
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
